export type TreeGeometryEvidenceStatus =
  | 'PASS'
  | 'FAIL'
  | 'WARNING'
  | 'INFO';

export const TREE_GEOMETRY_FAILURE_SOURCE_SCHEMA =
  'CC-TREE-LIVE-003G3E1' as const;

export const TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS =
  Object.freeze([
    'GEOMETRY-VIEWER-MISSING',
    'GEOMETRY-VIEWER-DISPLACED',
    'GEOMETRY-CARD-OVERLAP',
    'GEOMETRY-VIEWER-OVERLAP',
    'GEOMETRY-EDGE-ENDPOINT',
    'GEOMETRY-PARENT-ENDPOINT-AUTHORITY',
    'GEOMETRY-PARENT-CONNECTOR-MISSING',
    'GEOMETRY-PARENT-CONNECTOR-UNROUTED',
    'GEOMETRY-CONNECTOR-ROUTE-REJECTED',
    'GEOMETRY-CONNECTOR-ROUTE-MISSING',
    'GEOMETRY-SPOUSE-ENDPOINT',
    'GEOMETRY-GENERATION-ROW',
    'GEOMETRY-BOUNDS-CONTAINMENT',
    'GEOMETRY-LINE-CARD-INTERSECTION',
    'GEOMETRY-CONNECTOR-CROSSING',

    'TREE-RULE-GENERATION-ROW',
    'TREE-RULE-CHILD-BELOW-PARENT',
    'TREE-RULE-CHILD-WITHIN-PARENT-CLUSTER',
    'TREE-RULE-SIBLING-CLUSTER-CONTIGUOUS',
    'TREE-RULE-BRANCH-NONINTERLEAVING',
    'TREE-RULE-COPARENT-SHARED-CHILD',
    'TREE-RULE-SPOUSE-PARTNER-LATERAL',
    'TREE-RULE-VIEWER-CENTER',
    'TREE-RULE-CARD-NONOVERLAP',
  ] as const);

export type TreeGeometryRuntimeFatalRuleKey =
  typeof TREE_GEOMETRY_RUNTIME_FATAL_RULE_KEYS[number];

export const TREE_GEOMETRY_ROUTE_REJECTION_STATUSES =
  Object.freeze([
    'REJECTED-NONADJACENT-GENERATION',
    'REJECTED-ROOT-BOUNDARY',
    'REJECTED-OBSTACLE-INTERSECTION',
  ] as const);

export type TreeGeometryRouteRejectionStatus =
  typeof TREE_GEOMETRY_ROUTE_REJECTION_STATUSES[number];

export interface TreeGeometryPoint {
  x: number;
  y: number;
}

export interface TreeGeometryCard {
  memberId: string | null;
  x: number;
  y: number;
  tier: number;

  /**
   * Structural parent authority for this rendered member.
   *
   * Optional for historical verifier fixtures.
   * The live Tree runtime supplies this field for every
   * canonical rendered card.
   */
  parentIds?: string[];

  isYou?: boolean;
  needsConnection?: boolean;
}

export interface TreeGeometryEdge {
  fromMemberIds: string[];
  toMemberId: string;
  from: TreeGeometryPoint;
  to: TreeGeometryPoint;

  /**
   * Canonical connector root.
   *
   * Maternal, paternal, self, both, and descendant roots
   * represent generational ancestry authority.
   *
   * Partner-affiliated, child-mediated, affiliated, and
   * unresolved connectors do not acquire parent authority.
   */
  routeRoot?:
    | 'maternal'
    | 'paternal'
    | 'self'
    | 'both'
    | 'descendant'
    | 'partner-affiliated'
    | 'child-mediated'
    | 'affiliated'
    | 'unresolved';

  routeStatus?:
    | 'ROUTED'
    | 'REJECTED-NONADJACENT-GENERATION'
    | 'REJECTED-ROOT-BOUNDARY'
    | 'REJECTED-OBSTACLE-INTERSECTION';
  routePoints?: TreeGeometryPoint[];
  rejectedObstacleMemberIds?: string[];
  key?: string;
}

export interface TreeGeometrySpouseLink {
  aMemberId: string;
  bMemberId: string;
  a: TreeGeometryPoint;
  b: TreeGeometryPoint;
}

export interface TreeGeometryCompanionChip {
  memberId: string;
  anchorMemberId: string;
  stackIndex: number;
  x: number;
  top: number;
  width: number;
  height: number;
}

export interface TreeGeometryBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  w: number;
  h: number;
}

export interface TreeGeometryProjection {
  cards: TreeGeometryCard[];
  edges: TreeGeometryEdge[];
  spouseLinks: TreeGeometrySpouseLink[];
  companionChips?: TreeGeometryCompanionChip[];
  bounds: TreeGeometryBounds;
  requestedViewerMemberId?: string | null;
  centeredMemberId?: string | null;
}

export interface TreeGeometryEvidenceIssue {
  status: TreeGeometryEvidenceStatus;
  rule: string;
  details: string;
  memberIds?: string[];
  edgeKeys?: string[];

  /*
   * Structured private classification only.
   *
   * The runtime publication layer may aggregate this value,
   * but must never publish edge/member identity from the issue.
   */
  routeRejectionStatus?:
    TreeGeometryRouteRejectionStatus;
}

export interface TreeGeometryEvidenceReport {
  status: 'PASS' | 'FAIL';
  issues: TreeGeometryEvidenceIssue[];
  issueCount: number;
  cardCount: number;
  edgeCount: number;
  spouseLinkCount: number;
  companionChipCount: number;
  requestedViewerMemberId: string | null;
  renderedViewerMemberId: string | null;
  renderedViewerX: number | null;
  renderedViewerY: number | null;
  viewerOverlapMemberIds: string[];
  protectedViewerGeometryPassed: boolean;
}

export interface TreeGeometryEvidenceOptions {
  cardWidth: number;
  cardHeight: number;
  rowGap: number;
  tolerance?: number;
  bezierSamples?: number;
}

interface Rect {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const rectForCard = (
  card: TreeGeometryCard,
  width: number,
  height: number,
): Rect => ({
  minX: card.x - width / 2,
  maxX: card.x + width / 2,
  minY: card.y - height / 2,
  maxY: card.y + height / 2,
});

const rectsOverlap = (
  a: Rect,
  b: Rect,
  tolerance: number,
): boolean =>
  a.minX < b.maxX - tolerance &&
  a.maxX > b.minX + tolerance &&
  a.minY < b.maxY - tolerance &&
  a.maxY > b.minY + tolerance;

const pointInsideRect = (
  point: TreeGeometryPoint,
  rect: Rect,
  tolerance: number,
): boolean =>
  point.x > rect.minX + tolerance &&
  point.x < rect.maxX - tolerance &&
  point.y > rect.minY + tolerance &&
  point.y < rect.maxY - tolerance;

const pointInsideBounds = (
  point: TreeGeometryPoint,
  bounds: TreeGeometryBounds,
  tolerance: number,
): boolean =>
  point.x >= bounds.minX - tolerance &&
  point.x <= bounds.maxX + tolerance &&
  point.y >= bounds.minY - tolerance &&
  point.y <= bounds.maxY + tolerance;

const rectInsideBounds = (
  rect: Rect,
  bounds: TreeGeometryBounds,
  tolerance: number,
): boolean =>
  rect.minX >= bounds.minX - tolerance &&
  rect.maxX <= bounds.maxX + tolerance &&
  rect.minY >= bounds.minY - tolerance &&
  rect.maxY <= bounds.maxY + tolerance;

const bezierPoint = (
  from: TreeGeometryPoint,
  to: TreeGeometryPoint,
  t: number,
): TreeGeometryPoint => {
  const midY = (from.y + to.y) / 2;
  const oneMinusT = 1 - t;

  const x =
    oneMinusT ** 3 * from.x +
    3 * oneMinusT ** 2 * t * from.x +
    3 * oneMinusT * t ** 2 * to.x +
    t ** 3 * to.x;

  const y =
    oneMinusT ** 3 * from.y +
    3 * oneMinusT ** 2 * t * midY +
    3 * oneMinusT * t ** 2 * midY +
    t ** 3 * to.y;

  return { x, y };
};

const sampleEdge = (
  edge: TreeGeometryEdge,
  sampleCount: number,
): TreeGeometryPoint[] => {
  if (
    edge.routeStatus === 'ROUTED' &&
    Array.isArray(
      edge.routePoints,
    ) &&
    edge.routePoints.length >= 2
  ) {
    return edge.routePoints.map(
      point => ({
        x:
          point.x,
        y:
          point.y,
      }),
    );
  }

  if (
    edge.routeStatus &&
    edge.routeStatus !== 'ROUTED'
  ) {
    return [];
  }

  const points: TreeGeometryPoint[] = [];

  for (
    let index = 0;
    index <= sampleCount;
    index += 1
  ) {
    points.push(
      bezierPoint(
        edge.from,
        edge.to,
        index / sampleCount,
      ),
    );
  }

  return points;
};

const orientation = (
  a: TreeGeometryPoint,
  b: TreeGeometryPoint,
  c: TreeGeometryPoint,
): number =>
  (b.x - a.x) * (c.y - a.y) -
  (b.y - a.y) * (c.x - a.x);

const pointsNear = (
  a: TreeGeometryPoint,
  b: TreeGeometryPoint,
  tolerance: number,
): boolean =>
  Math.abs(a.x - b.x) <= tolerance &&
  Math.abs(a.y - b.y) <= tolerance;

const segmentsCross = (
  a1: TreeGeometryPoint,
  a2: TreeGeometryPoint,
  b1: TreeGeometryPoint,
  b2: TreeGeometryPoint,
  tolerance: number,
): boolean => {
  const o1 = orientation(a1, a2, b1);
  const o2 = orientation(a1, a2, b2);
  const o3 = orientation(b1, b2, a1);
  const o4 = orientation(b1, b2, a2);

  return (
    o1 * o2 < -tolerance &&
    o3 * o4 < -tolerance
  );
};

const shareEndpointAuthority = (
  a: TreeGeometryEdge,
  b: TreeGeometryEdge,
): boolean => {
  if (a.toMemberId === b.toMemberId) return true;

  if (a.fromMemberIds.includes(b.toMemberId)) return true;
  if (b.fromMemberIds.includes(a.toMemberId)) return true;

  return a.fromMemberIds.some((id) =>
    b.fromMemberIds.includes(id),
  );
};

export function verifyTreeGeometry(
  projection: TreeGeometryProjection,
  options: TreeGeometryEvidenceOptions,
): TreeGeometryEvidenceReport {
  const tolerance = options.tolerance ?? 0.001;
  const bezierSamples = options.bezierSamples ?? 32;
  const issues: TreeGeometryEvidenceIssue[] = [];

  const cards = Array.isArray(projection.cards)
    ? projection.cards
    : [];

  const edges = Array.isArray(projection.edges)
    ? projection.edges
    : [];

  const spouseLinks = Array.isArray(projection.spouseLinks)
    ? projection.spouseLinks
    : [];

  const companionChips = Array.isArray(
    projection.companionChips,
  )
    ? projection.companionChips
    : [];

  const visibleIds = new Set(
    cards
      .map((card) => card.memberId)
      .filter((id): id is string => Boolean(id)),
  );

  const requestedViewerMemberId =
    projection.requestedViewerMemberId ?? null;

  const renderedViewerCard =
    cards.find((card) => card.isYou) ?? null;

  const renderedViewerMemberId =
    renderedViewerCard?.memberId ?? null;

  const viewerOverlapMemberIds = new Set<string>();

  if (
    requestedViewerMemberId &&
    (
      !renderedViewerCard ||
      renderedViewerMemberId !== requestedViewerMemberId
    )
  ) {
    issues.push({
      status: 'FAIL',
      rule: 'GEOMETRY-VIEWER-MISSING',
      details:
        'The requested viewer is not the final rendered You card.',
      memberIds: [requestedViewerMemberId],
    });
  }

  const deliberateNonViewerCenter =
    Boolean(
      projection.centeredMemberId &&
      projection.centeredMemberId !==
        requestedViewerMemberId,
    );

  if (
    renderedViewerCard &&
    !deliberateNonViewerCenter &&
    (
      Math.abs(renderedViewerCard.x) > tolerance ||
      Math.abs(renderedViewerCard.y) > tolerance
    )
  ) {
    issues.push({
      status: 'FAIL',
      rule: 'GEOMETRY-VIEWER-DISPLACED',
      details:
        `Rendered viewer ${renderedViewerMemberId ?? 'unknown'} ` +
        `is at (${renderedViewerCard.x}, ${renderedViewerCard.y}) ` +
        'instead of the protected origin.',
      memberIds: renderedViewerMemberId
        ? [renderedViewerMemberId]
        : undefined,
    });
  }

  for (let i = 0; i < cards.length; i += 1) {
    const a = cards[i];
    if (!a.memberId) continue;

    const aRect = rectForCard(
      a,
      options.cardWidth,
      options.cardHeight,
    );

    for (let j = i + 1; j < cards.length; j += 1) {
      const b = cards[j];
      if (!b.memberId) continue;

      const bRect = rectForCard(
        b,
        options.cardWidth,
        options.cardHeight,
      );

      if (rectsOverlap(aRect, bRect, tolerance)) {
        issues.push({
          status: 'FAIL',
          rule: 'GEOMETRY-CARD-OVERLAP',
          details:
            `Rendered cards overlap: ` +
            `${a.memberId} and ${b.memberId}.`,
          memberIds: [a.memberId, b.memberId],
        });

        if (
          renderedViewerMemberId &&
          (
            a.memberId === renderedViewerMemberId ||
            b.memberId === renderedViewerMemberId
          )
        ) {
          const otherMemberId =
            a.memberId === renderedViewerMemberId
              ? b.memberId
              : a.memberId;

          viewerOverlapMemberIds.add(otherMemberId);

          issues.push({
            status: 'FAIL',
            rule: 'GEOMETRY-VIEWER-OVERLAP',
            details:
              `Rendered viewer ${renderedViewerMemberId} overlaps ` +
              `member ${otherMemberId}.`,
            memberIds: [
              renderedViewerMemberId,
              otherMemberId,
            ],
          });
        }
      }
    }
  }

  for (const edge of edges) {
    const missingSources = edge.fromMemberIds.filter(
      (id) => !visibleIds.has(id),
    );

    if (
      missingSources.length > 0 ||
      !visibleIds.has(edge.toMemberId)
    ) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-EDGE-ENDPOINT',
        details:
          `Edge ${edge.key ?? 'unknown'} references ` +
          `missing visible endpoint authority.`,
        memberIds: [
          ...missingSources,
          ...(
            visibleIds.has(edge.toMemberId)
              ? []
              : [edge.toMemberId]
          ),
        ],
        edgeKeys: edge.key ? [edge.key] : undefined,
      });
    }
  }

  /*
   * CC-TREE-LIVE-003G2Z
   *
   * PARENT_CONNECTOR_ENDPOINT_AUTHORITY
   *
   * A generational connector may only claim source members
   * that the rendered child structurally lists as parents.
   *
   * A spouse/partner relationship never grants parent-edge
   * authority by association.
   *
   * Historical geometry fixtures that predate structural
   * parent evidence remain readable because parentIds is
   * optional. The production FamilyTreeView path supplies
   * parentIds explicitly and therefore receives fail-closed
   * enforcement.
   */
  const ancestryConnectorRoots =
    new Set([
      'maternal',
      'paternal',
      'self',
      'both',
      'descendant',
    ]);

  /*
   * Index once rather than scanning all cards for every
   * connector. Parent-endpoint verification therefore
   * remains O(cards + edges).
   */
  const structuralCardByMemberId =
    new Map<string, TreeGeometryCard>(
      cards.flatMap(
        card =>
          card.memberId
            ? [[
                card.memberId,
                card,
              ] as const]
            : [],
      ),
    );

  for (const edge of edges) {
    if (
      !edge.routeRoot ||
      !ancestryConnectorRoots.has(
        edge.routeRoot,
      )
    ) {
      continue;
    }

    const child =
      structuralCardByMemberId.get(
        edge.toMemberId,
      ) ?? null;

    if (
      !child ||
      !Array.isArray(
        child.parentIds,
      )
    ) {
      continue;
    }

    const structuralParentIds =
      new Set(
        child.parentIds.filter(
          Boolean,
        ),
      );

    const unauthorizedSources =
      edge.fromMemberIds.filter(
        sourceId =>
          !structuralParentIds.has(
            sourceId,
          ),
      );

    if (
      unauthorizedSources.length >
      0
    ) {
      issues.push({
        status: 'FAIL',
        rule:
          'GEOMETRY-PARENT-ENDPOINT-AUTHORITY',
        details:
          `Connector ${edge.key ?? 'unknown'} claims ` +
          'non-parent generational endpoint authority.',
        memberIds: [
          ...unauthorizedSources,
          edge.toMemberId,
        ],
        edgeKeys:
          edge.key
            ? [edge.key]
            : undefined,
      });
    }
  }

  /*
   * CC-TREE-LIVE-003G3E
   *
   * VISIBLE_STRUCTURAL_PARENT_CONNECTOR_COMPLETENESS
   *
   * Production cards carry structural parentIds.
   *
   * For every visible structural parent -> child obligation:
   *
   * 1. an ancestry connector must exist;
   * 2. that connector must include the real parent endpoint;
   * 3. it must target the real child endpoint;
   * 4. at least one matching connector must be ROUTED with
   *    at least two route points.
   *
   * Hidden parent endpoints create no renderer obligation.
   */
  for (const child of cards) {
    if (
      !child.memberId ||
      !Array.isArray(child.parentIds)
    ) {
      continue;
    }

    const structuralParentIds =
      Array.from(
        new Set(
          child.parentIds.filter(
            Boolean,
          ),
        ),
      );

    for (const parentId of structuralParentIds) {
      if (!visibleIds.has(parentId)) {
        continue;
      }

      const matchingEdges =
        edges.filter(
          edge =>
            edge.toMemberId ===
              child.memberId &&
            edge.fromMemberIds.includes(
              parentId,
            ),
        );

      if (matchingEdges.length === 0) {
        issues.push({
          status: 'FAIL',
          rule:
            'GEOMETRY-PARENT-CONNECTOR-MISSING',
          details:
            'A visible structural parent-child obligation has no matching canonical ancestry connector.',
          memberIds: [
            parentId,
            child.memberId,
          ],
        });

        continue;
      }

      const hasUsableRoute =
        matchingEdges.some(
          edge =>
            edge.routeStatus ===
              'ROUTED' &&
            Array.isArray(
              edge.routePoints,
            ) &&
            edge.routePoints.length >=
              2,
        );

      if (!hasUsableRoute) {
        issues.push({
          status: 'FAIL',
          rule:
            'GEOMETRY-PARENT-CONNECTOR-UNROUTED',
          details:
            'A visible structural parent-child connector exists but has no usable routed geometry.',
          memberIds: [
            parentId,
            child.memberId,
          ],
          edgeKeys:
            matchingEdges.flatMap(
              edge =>
                edge.key
                  ? [edge.key]
                  : [],
            ),
        });
      }
    }
  }

  for (const edge of edges) {
    if (
      edge.routeStatus &&
      edge.routeStatus !== 'ROUTED'
    ) {
      issues.push({
        status:
          'FAIL',
        rule:
          'GEOMETRY-CONNECTOR-ROUTE-REJECTED',
        details:
          `Connector ${edge.key ?? 'unknown'} was rejected: ` +
          `${edge.routeStatus}.`,
        memberIds: [
          ...edge.fromMemberIds,
          edge.toMemberId,
          ...(edge.rejectedObstacleMemberIds ?? []),
        ],
        edgeKeys:
          edge.key
            ? [edge.key]
            : undefined,
        routeRejectionStatus:
          edge.routeStatus,
      });
    }

    if (
      edge.routeStatus === 'ROUTED' &&
      (
        !Array.isArray(
          edge.routePoints,
        ) ||
        edge.routePoints.length < 2
      )
    ) {
      issues.push({
        status:
          'FAIL',
        rule:
          'GEOMETRY-CONNECTOR-ROUTE-MISSING',
        details:
          `Connector ${edge.key ?? 'unknown'} is marked ROUTED ` +
          'without a usable orthogonal route.',
        memberIds: [
          ...edge.fromMemberIds,
          edge.toMemberId,
        ],
        edgeKeys:
          edge.key
            ? [edge.key]
            : undefined,
      });
    }
  }

  for (const link of spouseLinks) {
    if (
      !visibleIds.has(link.aMemberId) ||
      !visibleIds.has(link.bMemberId)
    ) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-SPOUSE-ENDPOINT',
        details:
          'Spouse link references a missing visible card.',
        memberIds: [
          link.aMemberId,
          link.bMemberId,
        ],
      });
    }
  }

  for (const card of cards) {
    if (!card.memberId) continue;

    const expectedY = -card.tier * options.rowGap;

    if (Math.abs(card.y - expectedY) > tolerance) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-GENERATION-ROW',
        details:
          `Member ${card.memberId} is at y=${card.y}; ` +
          `expected ${expectedY}.`,
        memberIds: [card.memberId],
      });
    }
  }

  for (const card of cards) {
    if (!card.memberId) continue;

    const rect = rectForCard(
      card,
      options.cardWidth,
      options.cardHeight,
    );

    if (
      !rectInsideBounds(
        rect,
        projection.bounds,
        tolerance,
      )
    ) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-BOUNDS-CONTAINMENT',
        details:
          `Final bounds exclude card ${card.memberId}.`,
        memberIds: [card.memberId],
      });
    }
  }

  for (const edge of edges) {
    const edgePoints =
      edge.routeStatus === 'ROUTED' &&
      Array.isArray(
        edge.routePoints,
      ) &&
      edge.routePoints.length >= 2
        ? edge.routePoints
        : [
            edge.from,
            edge.to,
          ];

    if (
      edgePoints.some(
        point =>
          !pointInsideBounds(
            point,
            projection.bounds,
            tolerance,
          ),
      )
    ) {
      issues.push({
        status:
          'FAIL',
        rule:
          'GEOMETRY-BOUNDS-CONTAINMENT',
        details:
          `Final bounds exclude edge ` +
          `${edge.key ?? 'unknown'} route geometry.`,
        edgeKeys:
          edge.key
            ? [edge.key]
            : undefined,
      });
    }
  }

  for (const link of spouseLinks) {
    if (
      !pointInsideBounds(
        link.a,
        projection.bounds,
        tolerance,
      ) ||
      !pointInsideBounds(
        link.b,
        projection.bounds,
        tolerance,
      )
    ) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-BOUNDS-CONTAINMENT',
        details:
          'Final bounds exclude spouse-link geometry.',
        memberIds: [
          link.aMemberId,
          link.bMemberId,
        ],
      });
    }
  }

  for (const chip of companionChips) {
    const chipRect: Rect = {
      minX: chip.x - chip.width / 2,
      maxX: chip.x + chip.width / 2,
      minY: chip.top,
      maxY: chip.top + chip.height,
    };

    if (
      !rectInsideBounds(
        chipRect,
        projection.bounds,
        tolerance,
      )
    ) {
      issues.push({
        status: 'FAIL',
        rule: 'GEOMETRY-BOUNDS-CONTAINMENT',
        details:
          `Final bounds exclude companion chip ` +
          `${chip.memberId}.`,
        memberIds: [
          chip.anchorMemberId,
          chip.memberId,
        ],
      });
    }
  }

  const cardById = new Map(
    cards
      .filter(
        (card): card is TreeGeometryCard & {
          memberId: string;
        } => Boolean(card.memberId),
      )
      .map((card) => [card.memberId, card]),
  );

  for (const edge of edges) {
    const samples = sampleEdge(edge, bezierSamples);
    const endpointIds = new Set([
      ...edge.fromMemberIds,
      edge.toMemberId,
    ]);

    for (const [memberId, card] of cardById) {
      if (endpointIds.has(memberId)) continue;

      const rect = rectForCard(
        card,
        options.cardWidth,
        options.cardHeight,
      );

      if (
        samples.some((point) =>
          pointInsideRect(point, rect, tolerance),
        )
      ) {
        issues.push({
          status: 'FAIL',
          rule: 'GEOMETRY-LINE-CARD-INTERSECTION',
          details:
            `Edge ${edge.key ?? 'unknown'} intersects ` +
            `unrelated card ${memberId}.`,
          memberIds: [memberId],
          edgeKeys: edge.key ? [edge.key] : undefined,
        });
      }
    }
  }

  const sampledEdges = edges.map((edge) => ({
    edge,
    points: sampleEdge(edge, bezierSamples),
  }));

  for (
    let i = 0;
    i < sampledEdges.length;
    i += 1
  ) {
    for (
      let j = i + 1;
      j < sampledEdges.length;
      j += 1
    ) {
      const a = sampledEdges[i];
      const b = sampledEdges[j];

      if (shareEndpointAuthority(a.edge, b.edge)) {
        continue;
      }

      let crossed = false;

      for (
        let ai = 1;
        ai < a.points.length - 1 && !crossed;
        ai += 1
      ) {
        for (
          let bi = 1;
          bi < b.points.length - 1;
          bi += 1
        ) {
          if (
            pointsNear(
              a.points[ai],
              b.points[bi],
              tolerance,
            )
          ) {
            crossed = true;
            break;
          }
        }
      }

      for (
        let ai = 0;
        ai < a.points.length - 1 && !crossed;
        ai += 1
      ) {
        for (
          let bi = 0;
          bi < b.points.length - 1;
          bi += 1
        ) {
          if (
            segmentsCross(
              a.points[ai],
              a.points[ai + 1],
              b.points[bi],
              b.points[bi + 1],
              tolerance,
            )
          ) {
            crossed = true;
            break;
          }
        }
      }

      if (crossed) {
        issues.push({
          status: 'FAIL',
          rule: 'GEOMETRY-CONNECTOR-CROSSING',
          details:
            `Connectors ${a.edge.key ?? 'unknown'} and ` +
            `${b.edge.key ?? 'unknown'} cross.`,
          edgeKeys: [
            a.edge.key ?? 'unknown',
            b.edge.key ?? 'unknown',
          ],
        });
      }
    }
  }

  const connectedIds = new Set<string>();

  for (const edge of edges) {
    for (const id of edge.fromMemberIds) {
      connectedIds.add(id);
    }
    connectedIds.add(edge.toMemberId);
  }

  for (const link of spouseLinks) {
    connectedIds.add(link.aMemberId);
    connectedIds.add(link.bMemberId);
  }

  for (const card of cards) {
    if (!card.memberId) continue;
    if (connectedIds.has(card.memberId)) continue;

    issues.push({
      status:
        card.isYou || card.needsConnection
          ? 'INFO'
          : 'WARNING',
      rule: 'GEOMETRY-ORPHAN-CONNECTOR',
      details:
        `Rendered member ${card.memberId} has no ` +
        `visible connector authority.`,
      memberIds: [card.memberId],
    });
  }

  const failed = issues.some(
    (issue) => issue.status === 'FAIL',
  );

  if (!failed) {
    issues.unshift({
      status: 'PASS',
      rule: 'TREE-GEOMETRY-EVIDENCE',
      details:
        'Final projection geometry passed all fatal rules.',
    });
  }

  return {
    status: failed ? 'FAIL' : 'PASS',
    issues,
    issueCount: issues.length,
    cardCount: cards.length,
    edgeCount: edges.length,
    spouseLinkCount: spouseLinks.length,
    companionChipCount: companionChips.length,
    requestedViewerMemberId,
    renderedViewerMemberId,
    renderedViewerX: renderedViewerCard?.x ?? null,
    renderedViewerY: renderedViewerCard?.y ?? null,
    viewerOverlapMemberIds:
      Array.from(viewerOverlapMemberIds).sort(),
    protectedViewerGeometryPassed:
      !issues.some(
        (issue) =>
          issue.rule === 'GEOMETRY-VIEWER-MISSING' ||
          issue.rule === 'GEOMETRY-VIEWER-DISPLACED' ||
          issue.rule === 'GEOMETRY-VIEWER-OVERLAP',
      ),
  };
}
